import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BudgetService } from '@/services/BudgetService';
import { ApiException } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { 
  Budget, 
  CreateBudgetRequest, 
  UpdateBudgetRequest,
  CopyBudgetRequest,
  ConflictError,
} from '@/types';

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiException) {
    return error.error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error';
}

// Query keys
export const budgetKeys = {
  all: ['budgets'] as const,
  active: () => [...budgetKeys.all, 'active'] as const,
  summaries: () => [...budgetKeys.all, 'summaries'] as const,
  activeSummaries: () => [...budgetKeys.all, 'summaries', 'active'] as const,
  detail: (id: string) => [...budgetKeys.all, 'detail', id] as const,
  dashboardSummaries: (userId?: string) =>
    [...budgetKeys.all, 'dashboard', 'summaries', userId ?? 'anonymous'] as const,
  dashboardStats: (userId?: string) =>
    [...budgetKeys.all, 'dashboard', 'stats', userId ?? 'anonymous'] as const,
};

/**
 * Hook for fetching dashboard budget summaries (single paginated source).
 */
export function useDashboardBudgetSummaries() {
  const { user } = useAuth();

  return useQuery({
    queryKey: budgetKeys.dashboardSummaries(user?.id),
    queryFn: () => BudgetService.getDashboardSummaries(),
    enabled: !!user,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for fetching active budget summaries (dashboard).
 */
export function useActiveBudgetSummaries() {
  const { user } = useAuth();

  return useQuery({
    queryKey: budgetKeys.activeSummaries(),
    queryFn: () => BudgetService.getActiveSummaries(user?.id ?? ''),
    enabled: !!user,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for fetching all budget summaries (dashboard).
 */
export function useBudgetSummaries() {
  const { user } = useAuth();

  return useQuery({
    queryKey: budgetKeys.summaries(),
    queryFn: () => BudgetService.getSummaries(user?.id ?? ''),
    enabled: !!user,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for fetching all budgets.
 */
export function useBudgets() {
  const { user } = useAuth();

  return useQuery({
    queryKey: budgetKeys.all,
    queryFn: () => BudgetService.getAll(user?.id ?? ''),
    enabled: !!user,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook for fetching active budgets.
 */
export function useActiveBudgets() {
  const { user } = useAuth();

  return useQuery({
    queryKey: budgetKeys.active(),
    queryFn: () => BudgetService.getActive(user?.id ?? ''),
    enabled: !!user,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for fetching a single budget.
 */
export function useBudget(id: string | undefined) {
  return useQuery({
    queryKey: budgetKeys.detail(id ?? ''),
    queryFn: () => BudgetService.getById(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook for dashboard statistics.
 */
export function useDashboardStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: budgetKeys.dashboardStats(user?.id),
    queryFn: () => BudgetService.getDashboardStats(user?.id ?? ''),
    enabled: !!user,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook for creating a budget.
 */
export function useCreateBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateBudgetRequest) => BudgetService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.dashboardSummaries() });
      toast.success('Budget created successfully');
    },
    onError: (error) => {
      toast.error(`Error creating budget: ${getErrorMessage(error)}`);
    },
  });
}

/**
 * Hook for updating a budget.
 */
export function useUpdateBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateBudgetRequest }) =>
      BudgetService.update(id, request),
    onSuccess: (budget) => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.dashboardSummaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.detail(budget.id) });
      toast.success('Budget updated successfully');
    },
    onError: async (error, variables) => {
      if (error instanceof ApiException && error.isConflict) {
        const conflict = error.error as Partial<ConflictError>;
        const entityId = conflict.entityId ?? variables.id;

        await queryClient.invalidateQueries({ queryKey: budgetKeys.detail(entityId) });
        await queryClient.invalidateQueries({ queryKey: budgetKeys.all });
        await queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
        await queryClient.invalidateQueries({ queryKey: budgetKeys.activeSummaries() });

        toast.error(
          conflict.message ??
            'This budget was updated by someone else. Latest data was reloaded; review and try again.'
        );
        return;
      }

      toast.error(`Error updating budget: ${getErrorMessage(error)}`);
    }
  });
}

/**
 * Hook for deleting a budget.
 */
export function useDeleteBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, confirmation }: { id: string; confirmation?: boolean }) =>
      BudgetService.delete(id, confirmation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.dashboardSummaries() });
      toast.success('Budget deleted successfully');
    },
    onError: (error) => {
      toast.error(`Error deleting budget: ${getErrorMessage(error)}`);
    }
  });
}

/**
 * Hook for copying a budget to a new period.
 */
export function useCopyBudget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CopyBudgetRequest) => BudgetService.copyToNewPeriod(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: budgetKeys.dashboardSummaries() });
      toast.success('Budget copied successfully');
    },
    onError: (error) => {
      toast.error(`Error copying budget: ${getErrorMessage(error)}`);
    }
  });
}

/**
 * Hook for budget validation.
 */
export function useBudgetValidation() {
  return {
    validate: BudgetService.validate,
    calculateStatus: BudgetService.calculateStatus,
    toSummary: BudgetService.toSummary,
  };
}

/**
 * Hook for optimistic budget updates.
 */
export function useOptimisticBudgetUpdate() {
  const queryClient = useQueryClient();

  const setOptimistic = (budget: Budget) => {
    queryClient.setQueryData(budgetKeys.detail(budget.id), budget);
    
    queryClient.setQueryData<Budget[]>(budgetKeys.all, (old) => {
      if (!old) return [budget];
      const index = old.findIndex((b) => b.id === budget.id);
      if (index === -1) return [...old, budget];
      const newData = [...old];
      newData[index] = budget;
      return newData;
    });
  };

  const rollback = (id: string, previousBudget: Budget | undefined) => {
    if (previousBudget) {
      queryClient.setQueryData(budgetKeys.detail(id), previousBudget);
    } else {
      queryClient.removeQueries({ queryKey: budgetKeys.detail(id) });
    }
    queryClient.invalidateQueries({ queryKey: budgetKeys.all });
  };

  return { setOptimistic, rollback };
}
